import type { ReactNode } from 'react';
import {
  Lightbulb,
  Info,
  NotePencil,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';

// Callout block rendered from `:::tip`/`:::info`/`:::note` container directives in
// docs markdown (see remarkDocCallouts). Each type gets its own accent color and
// icon; the title is required at authoring time so the header is always present.
export type DocCalloutType = 'tip' | 'info' | 'note';

const CALLOUT_ICON: Record<DocCalloutType, PhosphorIcon> = {
  tip: Lightbulb,
  info: Info,
  note: NotePencil,
};

// Fallback heading used only when a callout has no title.
function defaultLabel(type: DocCalloutType): string {
  switch (type) {
    case 'tip':
      return 'Tip';
    case 'info':
      return 'Info';
    case 'note':
      return 'Note';
  }
}

interface DocCalloutProps {
  type?: string;
  title?: string;
  children?: ReactNode;
}

function DocCallout({ type, title, children }: DocCalloutProps) {
  // Fall back to `note` styling if an unknown type slips through; the remark
  // plugin only emits the three known types, so this is just defensive.
  const calloutType: DocCalloutType =
    type === 'tip' || type === 'info' || type === 'note' ? type : 'note';
  const IconComponent = CALLOUT_ICON[calloutType];

  return (
    <div className={`docs-callout docs-callout-${calloutType}`} role="note">
      <p className="docs-callout-title">
        <IconComponent className="docs-callout-icon" weight="fill" aria-hidden="true" />
        <span>{title || defaultLabel(calloutType)}</span>
      </p>
      <div className="docs-callout-body">{children}</div>
    </div>
  );
}

export default DocCallout;
